import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Select from "@amzn/awsui-components-react/polaris/select";
import Button from "@amzn/awsui-components-react/polaris/button";
import Box from "@amzn/awsui-components-react/polaris/box";

/** One medication option, as loaded from GET /drug/get-drugs into RecipeBuilder state. */
export interface MedicationOption {
  drugId: string;
  description: string;
  defaultOption?: boolean;
  allowFakeout?: boolean;
}

export interface MedicationListSectionProps {
  /** Wizard step number for the header (RecipeBuilder owns step bookkeeping). */
  stepNumber: number;
  /** All medications available to pick from (shared, loaded once by the parent). */
  medications: MedicationOption[];
  /** The recipe's legacy medicationList (drugIds). */
  medicationList: string[];
  /** The currently-staged "Add" selection, or null. */
  selectedMedication: { label: string; value: string } | null;
  /** eScribe greys the editor: its Rx is a fixed, verified med, not driven by this list. */
  disabled: boolean;
  /** Drug IDs that also appear in the NewRx list — flagged inline (the backend rejects the overlap). */
  conflictingDrugIds?: string[];
  onSelectedMedicationChange: (option: { label: string; value: string } | null) => void;
  onMedicationListChange: (medicationList: string[]) => void;
}

/**
 * Legacy medication-list editor for the CPCP recipe Medications step (script-chase / health-profile meds).
 *
 * Controlled: the parent (RecipeBuilder) owns `medicationList` / `selectedMedication` state and passes change
 * callbacks, matching the parent-orchestrator + controlled-child pattern used elsewhere. Extracted out of
 * RecipeBuilder to keep that file manageable and to sit beside the NewRx (PMGS) editor in the same step.
 */
export default class MedicationListSection extends React.Component<MedicationListSectionProps> {
  private addSelected = () => {
    const { selectedMedication, medicationList, onMedicationListChange, onSelectedMedicationChange } = this.props;
    if (selectedMedication && !medicationList.includes(selectedMedication.value)) {
      onMedicationListChange([...medicationList, selectedMedication.value]);
      onSelectedMedicationChange(null);
    }
  };

  private remove = (drugId: string) => {
    const { medicationList, onMedicationListChange } = this.props;
    onMedicationListChange(medicationList.filter((id) => id !== drugId));
  };

  render() {
    const {
      stepNumber,
      medications,
      medicationList,
      selectedMedication,
      disabled,
      conflictingDrugIds = [],
      onSelectedMedicationChange,
    } = this.props;
    const medOptions = medications.map((m) => ({ label: m.description, value: m.drugId }));
    const conflictSet = new Set(conflictingDrugIds);

    return (
      <Container
        header={
          <Header variant="h3" description="Legacy medication list (script chase / health profile)">
            Step {stepNumber}: Prescriptions
          </Header>
        }
      >
        <SpaceBetween size="m">
          <FormField
            label={<b>Medications or Vitamins (Optional)</b>}
            constraintText={
              disabled
                ? "Not applicable for eScribe — the ingested Rx is a fixed, verified medication (Atorvastatin 20 MG)."
                : undefined
            }
          >
            <SpaceBetween size="s">
              <SpaceBetween size="xs" direction="horizontal">
                <div style={{ flexGrow: 1 }}>
                  <Select
                    selectedOption={selectedMedication}
                    onChange={({ detail }) => onSelectedMedicationChange(detail.selectedOption as any)}
                    options={medOptions}
                    placeholder="Select a medication"
                    filteringType="auto"
                    expandToViewport
                    disabled={disabled}
                  />
                </div>
                <Button disabled={disabled || !selectedMedication} onClick={this.addSelected}>
                  Add
                </Button>
              </SpaceBetween>
              {medicationList.length > 0 && (
                <SpaceBetween size="xs">
                  {medicationList.map((drugId) => {
                    const med = medications.find((m) => m.drugId === drugId);
                    const conflict = conflictSet.has(drugId);
                    return (
                      <SpaceBetween key={drugId} size="xs" direction="horizontal">
                        <Box color={conflict ? "text-status-error" : undefined}>
                          {med ? med.description : drugId}
                          {conflict ? " — also in NewRx; remove from one list" : ""}
                        </Box>
                        <Button
                          variant="icon"
                          iconName="close"
                          disabled={disabled}
                          onClick={() => this.remove(drugId)}
                        />
                      </SpaceBetween>
                    );
                  })}
                </SpaceBetween>
              )}
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Container>
    );
  }
}
